import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import {
  resolveAudioUrl,
  isEffectivelyMuted,
  subscribeChannelMute,
} from "../../utils/gameAudio";
import { isLoginExemptPath } from "../../utils/publicAccessPaths";

const SOCKET_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const isUserLoggedIn = () => {
  try {
    return !!localStorage.getItem("auth_token");
  } catch {
    return false;
  }
};

/** Unlock HTMLAudio so socket-triggered play() works after first tap/click */
let audioUnlocked = false;
const unlockAudio = () => {
  if (audioUnlocked) return;
  try {
    const silent = new Audio(
      "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA="
    );
    silent.volume = 0.01;
    silent
      .play()
      .then(() => {
        silent.pause();
        audioUnlocked = true;
      })
      .catch(() => {});
  } catch {
    /* ignore */
  }
};

const SoundPaletteListener = () => {
  const location = useLocation();
  const socketRef = useRef(null);
  const audioRef = useRef(null);
  const pathRef = useRef(location.pathname);

  pathRef.current = location.pathname;

  const stopCurrentSound = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch {
        /* ignore */
      }
      audioRef.current = null;
    }
  }, []);

  const shouldPlayOnCurrentPage = useCallback(() => {
    // Skip login/signup/admin/ticket exempt pages — play on user pages like /mindyourmusic
    if (isLoginExemptPath(pathRef.current)) {
      return false;
    }
    if (!isUserLoggedIn()) {
      return false;
    }
    return true;
  }, []);

  const playSound = useCallback(
    (soundData) => {
      if (!shouldPlayOnCurrentPage()) {
        return;
      }

      if (isEffectivelyMuted()) {
        return;
      }

      stopCurrentSound();

      try {
        const url = resolveAudioUrl(soundData.audio_url);
        if (!url) return;

        const audio = new Audio(url);
        audio.volume = soundData.volume ?? 1;

        audio.play().catch((err) => {
          console.warn(
            "SoundPaletteListener: Failed to play sound (tap the page once to unlock audio):",
            err
          );
        });

        audioRef.current = audio;

        audio.addEventListener("ended", () => {
          audioRef.current = null;
        });
      } catch (err) {
        console.error("SoundPaletteListener: Error playing sound:", err);
      }
    },
    [shouldPlayOnCurrentPage, stopCurrentSound]
  );

  useEffect(
    () =>
      subscribeChannelMute(() => {
        if (isEffectivelyMuted()) stopCurrentSound();
      }),
    [stopCurrentSound]
  );

  // Unlock audio on first user interaction (required by browsers for remote play)
  useEffect(() => {
    const onInteract = () => unlockAudio();
    window.addEventListener("pointerdown", onInteract, { once: true, capture: true });
    window.addEventListener("keydown", onInteract, { once: true, capture: true });
    return () => {
      window.removeEventListener("pointerdown", onInteract, { capture: true });
      window.removeEventListener("keydown", onInteract, { capture: true });
    };
  }, []);

  useEffect(() => {
    socketRef.current = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      withCredentials: true,
    });

    socketRef.current.on("sound_palette:play", (data) => {
      playSound(data);
    });

    socketRef.current.on("sound_palette:stop", () => {
      stopCurrentSound();
    });

    return () => {
      stopCurrentSound();
      if (socketRef.current) {
        socketRef.current.off("sound_palette:play");
        socketRef.current.off("sound_palette:stop");
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [playSound, stopCurrentSound]);

  return null;
};

export default SoundPaletteListener;
